import React from "react"
import { View, Text } from "react-native";
import CloverPoint from "types/models/CloverPoint";
import { StyleSheet } from "react-native";
import CloverConnectionStatusButton from "./CloverConnectionStatusButton";
import { colors } from "../../config";
import { CloverPointConnectionStatusType } from "types/enums/CloverPointConnectionStatusType";
import { MaterialIcons } from "@expo/vector-icons";
import Venue from "types/models/Venue";

export default ({ 
    cloverPoints, 
    appColor, 
    mediumScreen,
    t,
    currentVenue,
}: { 
    cloverPoints: CloverPoint[], 
    mediumScreen: boolean,
    t: any,
    appColor?: string,
    currentVenue: Venue | null,
}): JSX.Element => {
    const {headers, cellsFlex} = React.useMemo(() => {
        return {
            headers: [t('clover-connection-status.serial'), t('clover-connection-status.vinculated-at') , t('clover-connection-status.description'), t('clover-connection-status.connection-type'), t('clover-connection-status.connection-statuses'), t('clover-connection-status.paper')],
            cellsFlex: [1.5, 1.5, 1.5, 1.5, 0.8, 0.8]
        }
    }, []);

    type Hex = string
    type IconName = React.ComponentProps<typeof MaterialIcons>['name'] 

    const getStatusOpts = (cp: CloverPoint): { color: Hex, iconName: IconName } => {
        if (!cp.isActive)
            return { color: colors.black, iconName: 'block' }

        switch (cp.deviceConnectionStatus) {
            case CloverPointConnectionStatusType.CONNECTED:
            return { color: colors.cloverConnected, iconName: 'wifi' }

            case CloverPointConnectionStatusType.UNSTABLE:
            return { color: colors.cloverUnstable, iconName: 'perm-scan-wifi' }

            case CloverPointConnectionStatusType.DISCONNECTED:
            return { color: colors.cloverDisconnected, iconName: 'wifi-off' }

            default:
            return { color: colors.gray, iconName: 'help' }
        }
    }

    const renderStatusButton = (cloverPoint: CloverPoint) => {
        const opts = getStatusOpts(cloverPoint)
        return (
            <CloverConnectionStatusButton
                borderColor={''}
                backgroundColor={opts.color}
                fillColor={"#fff"}
                iconName={opts.iconName}
            />
        )
    }

    const renderPaperButton = (cloverPoint: CloverPoint) => {
        const hasPaper = cloverPoint.deviceHasPaper;
        const iconName: React.ComponentProps<typeof MaterialIcons>['name'] = hasPaper ? 'print' : 'print-disabled';
        const color = hasPaper ? colors.cloverConnected : colors.cloverDisconnected;
        return (
            <CloverConnectionStatusButton
                borderColor={color}
                backgroundColor={"#fff"}
                fillColor={color}
                iconName={iconName}
            />
        )
    }

    const getDeviceConnectionStatusColor = (cloverPoint: CloverPoint) => {
        if (cloverPoint.deviceConnectionStatus === CloverPointConnectionStatusType.CONNECTED) {
            return colors.cloverConnected;
        }else if (cloverPoint.deviceConnectionStatus === CloverPointConnectionStatusType.UNSTABLE) {
            return colors.cloverUnstable;
        }else {
            return colors.cloverDisconnected;
        }
    }

    const getDevicePairedSourceName = (cloverPoint: CloverPoint): string => {
        if (!currentVenue) return '';
        const code = cloverPoint.pairingOriginCode;
        if (currentVenue.code === code) {
            return currentVenue.name;
        }else{
            return currentVenue.commerces.find((commerce) => commerce.code === code)?.name ?? '';
        }
    }
    return (
        <View style={{marginBottom: 20}}>
            {mediumScreen ? (
                <>
                <View style={{...styles.row, backgroundColor: appColor ?? '#001639'}}>
                {headers.map((header, index) => {
                    return (
                        <Text style={{...styles.textHeader, flex: cellsFlex[index]}} key={index}>{header}</Text>
                    )
                })}
                </View>
                <View style={{ backgroundColor: colors.lightGray}}>
                    {cloverPoints.map((cloverPoint, index) => {
                        return (
                            <View key={index} style={{...styles.row, flex: cellsFlex[index]}}>
                                <Text style={{...styles.textRow, flex: cellsFlex[0]}}>{cloverPoint.serial ?? '-'}</Text>
                                <Text style={{...styles.textRow, flex: cellsFlex[1]}}>{getDevicePairedSourceName(cloverPoint)}</Text>
                                <Text style={{...styles.textRow, flex: cellsFlex[2]}}>{cloverPoint.description ?? '-'}</Text>
                                <Text style={{...styles.textRow, flex: cellsFlex[3]}}>{cloverPoint.deviceConnectionType ?? '-'}</Text>
                                <Text style={{...styles.textRow, flex: cellsFlex[4]}}>{renderStatusButton(cloverPoint)}</Text>
                                <Text style={{...styles.textRow, flex: cellsFlex[5]}}>{renderPaperButton(cloverPoint)}</Text>
                            </View>
                        )
                    })}
                </View>
                </>
            ): (
                <>
                    <View>
                        {cloverPoints.map((cloverPoint, index) => {
                            return (
                                <View key={index} style={{...styles.cardContainer}}>
                                    <View style={{...styles.headerContainer, backgroundColor: getDeviceConnectionStatusColor(cloverPoint)}}>
                                        <Text style={{...styles.textHeader, flex: 1}}>{cloverPoint.serial ?? '-'}</Text>
                                    </View>
                                    <View style={styles.cardInfoContainer}>
                                        <View style={styles.cardTextsContainer}>
                                            <Text  style={styles.textRow}>{getDevicePairedSourceName(cloverPoint)}</Text>
                                            <Text style={styles.textRow}>{cloverPoint.deviceConnectionType ?? '-'}</Text>
                                        </View>
                                        <View style={{flexDirection: 'row'}}>
                                            <Text style={{marginHorizontal: 5}}>{renderStatusButton(cloverPoint)}</Text>
                                            <Text style={{marginHorizontal: 5}}>{renderPaperButton(cloverPoint)}</Text>
                                        </View>
                                    </View>
                                </View>
                            )
                        })}
                    </View>
                </>
                )}
                        
        </View>
    )
}

const styles = StyleSheet.create({
    tableContainer: { 
        flex: 1, 
        backgroundColor: '#fff', 
        borderRadius: 10, 
        marginTop: 10, 
        marginBottom: 10, 
        padding: 10, 
    },
    row: { 
        paddingVertical: 5,
        paddingHorizontal: 15,
        flexDirection: 'row', 
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: colors.gray,
        minHeight: 60,
        maxHeight: 60,
    },
    textHeader: { 
        fontWeight: '500', 
        fontSize: 16, 
        color: '#fff', 
    },
    textRow: { 
        fontWeight: '500', 
        fontSize: 16, 
        color: '#464646ff', 
    },
    cardContainer:{
        flexDirection: 'column', 
        alignItems: 'center',
        backgroundColor: colors.lightGray,
        borderRadius: 10,
        marginVertical: 10,
        overflow: 'hidden',
    },
    headerContainer: {
        width: '100%',
        height: 30,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cardInfoContainer: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 10,
    },
    cardTextsContainer: {
        flexDirection: 'column',
        alignItems: 'center',
    },
  });